import { use, useEffect, useState } from 'react';
import { FieldValues, useForm, UseFormReturn } from 'react-hook-form';
import ConfirmationPopupProviderContext from 'shared/components/confirmation-pop-up/providers/contexts/confirmation-popup-provider-context';
import ErrorMessageProviderContext from 'shared/components/error/providers/contexts/error-message-context';
import ViewingMode from 'shared/components/viewing-mode-controller/types/viewing-mode';

import { CallPlanForm } from 'features/care-connect-settings/domain/models/call-plan-form';
import { ReadCallPlanResponse } from 'features/care-connect-settings/domain/models/call-plan-response';
import { RingingStrategy } from 'features/care-connect-settings/domain/models/ringing-strategy';
import { SubscribableOrganisationUnit } from 'features/care-connect-settings/domain/models/subscribable-organisation-unit-tree-response';
import { UpsertCallPlanResponse, UpsertCallPlanResultType } from 'features/care-connect-settings/domain/models/upsert-call-plan-response';
import { VcareNumberType } from 'features/care-connect-settings/domain/models/vcare-number-type';
import { useReadCallPlan, useRemoveCallPlan, useUpsertCallPlan } from 'features/care-connect-settings/domain/queries/call-plan-queries';

export type CallPlanDetails = {
  callPlanForm: UseFormReturn<CallPlanForm>;
  isEditing: boolean;
  readCallPlanData: ReadCallPlanResponse | null | undefined;
  readCallPlanIsLoading: boolean;
  readCallPlanIsFetching: boolean;
  upsertCallPlanIsPending: boolean;
  removeCallPlanIsPending: boolean;
  callPlanIsLoading: boolean;
  isUpsertCallPlanErrorPopupOpen: boolean;
  handleCallPlanEdit: () => void;
  handleCallPlanCancel: () => void;
  cancelEditing: () => void;
  closeUpsertCallPlanErrorPopup: () => void;
  submitCallPlan: (fieldValues: FieldValues) => void;
};

const useCallPlanDetails = (
  selectedOrganisationUnit: SubscribableOrganisationUnit | undefined,
  organisationUnits: SubscribableOrganisationUnit[] | undefined,
): CallPlanDetails => {
  const { setErrorMessage } = use(ErrorMessageProviderContext);
  const { enablePopUpGuard } = use(ConfirmationPopupProviderContext);
  const organisationUnitId = selectedOrganisationUnit?.isRoot ? undefined : selectedOrganisationUnit?.id;

  const findOrganisationUnit = (
    organisationUnitsToSearch: SubscribableOrganisationUnit[],
    organisationUnitIdToFind: string,
  ): SubscribableOrganisationUnit | undefined => {
    for (const organisationUnit of organisationUnitsToSearch) {
      if (organisationUnit.id === organisationUnitIdToFind) {
        return organisationUnit;
      }

      const childOrganisationUnit = findOrganisationUnit(organisationUnit.childSubscriptionChannels, organisationUnitIdToFind);
      if (childOrganisationUnit) {
        return childOrganisationUnit;
      }
    }

    return undefined;
  };

  const getDefaultCallPlanLabel = (): string => {
    if (!selectedOrganisationUnit) {
      return '';
    }

    if (selectedOrganisationUnit.isLocation) {
      return selectedOrganisationUnit.name;
    }

    const location = selectedOrganisationUnit.parentLocationId
      ? findOrganisationUnit(organisationUnits ?? [], selectedOrganisationUnit.parentLocationId)
      : undefined;

    return location ? `${location.name} | ${selectedOrganisationUnit.name}` : '';
  };

  const [callPlanViewingMode, setCallPlanViewingMode] = useState<ViewingMode>('viewing');
  const [isUpsertCallPlanErrorPopupOpen, setIsUpsertCallPlanErrorPopupOpen] = useState(false);

  const defaultValues: CallPlanForm = {
    isCallPlanEnabled: false,
    externalNumbers: [],
    internalNumbers: [],
    label: getDefaultCallPlanLabel(),
    ringingStrategy: '',
  };

  const callPlanForm = useForm({
    defaultValues: defaultValues,
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  const {
    data: readCallPlanData,
    isLoading: readCallPlanIsLoading,
    isFetching: readCallPlanIsFetching,
    error: readCallPlanError,
  } = useReadCallPlan(organisationUnitId);

  const {
    mutate: upsertCallPlan,
    error: upsertCallPlanError,
    isError: upsertCallPlanIsError,
    isPending: upsertCallPlanIsPending,
  } = useUpsertCallPlan();

  const {
    mutate: removeCallPlan,
    error: removeCallPlanError,
    isError: removeCallPlanIsError,
    isPending: removeCallPlanIsPending,
  } = useRemoveCallPlan();

  useEffect(() => {
    if (readCallPlanError) {
      setErrorMessage(readCallPlanError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readCallPlanError]);

  useEffect(() => {
    if (upsertCallPlanIsError) {
      setErrorMessage(upsertCallPlanError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upsertCallPlanIsError]);

  useEffect(() => {
    if (removeCallPlanIsError) {
      setErrorMessage(removeCallPlanError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [removeCallPlanIsError]);

  useEffect(() => {
    if (!organisationUnitId) {
      return;
    }

    if (readCallPlanData) {
      callPlanForm.reset({ ...readCallPlanData, isCallPlanEnabled: true });
    } else if (readCallPlanData === null) {
      callPlanForm.reset(defaultValues);
    }
  }, [readCallPlanData, organisationUnitId]);

  useEffect(() => {
    if (organisationUnitId) {
      setCallPlanViewingMode('viewing');
    }
  }, [organisationUnitId]);

  useEffect(() => {
    enablePopUpGuard(callPlanForm.formState.isDirty);
  }, [callPlanForm.formState.isDirty]);

  const resetView = (): void => {
    callPlanForm.reset(readCallPlanData ? { ...readCallPlanData, isCallPlanEnabled: true } : defaultValues);
    setCallPlanViewingMode('viewing');
  };

  const handleUpsertCallPlanResponse = (response: UpsertCallPlanResponse): void => {
    resetView();

    if (
      response.result === UpsertCallPlanResultType.VcareConnectorAuthenticationNotFound ||
      response.result === UpsertCallPlanResultType.FailedToUpsertQueue ||
      response.result === UpsertCallPlanResultType.FailedToUpdateNumbers
    ) {
      setIsUpsertCallPlanErrorPopupOpen(true);
    }
  };

  const closeUpsertCallPlanErrorPopup = (): void => setIsUpsertCallPlanErrorPopupOpen(false);

  const cancelEditing = (): void => {
    callPlanForm.reset(defaultValues);
    setCallPlanViewingMode('viewing');
  };

  const handleCallPlanEdit = (): void => {
    if (callPlanForm.getValues('externalNumbers').length === 0) {
      callPlanForm.setValue('externalNumbers', [{ number: '', isUsedForOutGoingCalls: true }], { shouldDirty: false });
    }

    if (callPlanForm.getValues('internalNumbers').length === 0) {
      callPlanForm.setValue('internalNumbers', [{ number: '', isUsedForOutGoingCalls: false }], { shouldDirty: false });
    }

    setCallPlanViewingMode('editing');
  };

  const handleCallPlanCancel = (): void => {
    resetView();
  };

  const submitCallPlan = (fieldValues: FieldValues): void => {
    if (!organisationUnitId) {
      return;
    }

    const { isCallPlanEnabled, externalNumbers, internalNumbers, label, ringingStrategy } = fieldValues as CallPlanForm;

    if (isCallPlanEnabled) {
      upsertCallPlan(
        {
          organisationUnitId: organisationUnitId,
          label,
          ringingStrategy: ringingStrategy as RingingStrategy,
          numbers: [
            ...internalNumbers.map(({ number, isUsedForOutGoingCalls }) => ({
              number,
              isUsedForOutGoingCalls,
              type: VcareNumberType.Internal,
            })),
            ...externalNumbers.map(({ number, isUsedForOutGoingCalls }) => ({
              number,
              isUsedForOutGoingCalls,
              type: VcareNumberType.External,
            })),
          ],
        },
        { onSuccess: handleUpsertCallPlanResponse, onError: resetView },
      );
    } else {
      removeCallPlan(organisationUnitId, { onSuccess: resetView });
    }
  };

  return {
    callPlanForm,
    isEditing: callPlanViewingMode === 'editing',
    readCallPlanData,
    readCallPlanIsLoading,
    readCallPlanIsFetching,
    upsertCallPlanIsPending,
    removeCallPlanIsPending,
    callPlanIsLoading: readCallPlanIsFetching || upsertCallPlanIsPending || removeCallPlanIsPending,
    isUpsertCallPlanErrorPopupOpen,
    handleCallPlanEdit,
    handleCallPlanCancel,
    cancelEditing,
    closeUpsertCallPlanErrorPopup,
    submitCallPlan,
  };
};

export default useCallPlanDetails;
